import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";

export const fetchProductSizeData = createAsyncThunk(
  "getProductSize",
  async (
    { formData, colorName }: { formData: FormData; colorName: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/getProductSize`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return { data: response.data, colorName };
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

export const fetchProductTierPrice = createAsyncThunk(
  "getProductTierPrice",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/getProductTierPrice`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      throw rejectWithValue((error as AxiosError)?.response?.data);
    }
  },
);

interface productSizeState {
  loadingproductSize: boolean;
  loadingproductTierPrice: boolean;
  productSizeData: { [key: string]: any };
  productTierPriceData: any;
  error: string;
}

const initialState: productSizeState = {
  loadingproductSize: false,
  loadingproductTierPrice: false,
  productSizeData: [],
  productTierPriceData: null,
  error: "",
};

export const productSizeSlice = createSlice({
  name: "size",
  initialState,
  reducers: {
    removeProductSizeData: (state, action) => {
      state.productSizeData = state.productSizeData.filter(
        (item: any) => !item[action.payload],
      );
    },

    resetProductSizeData: (state) => {
      state.productSizeData = [];
    },
    updateVariantOption: (state, action) => {
      const { colorName, variantId, value } = action.payload;
      const productSize = state.productSizeData.find(
        (item: any) => item[colorName],
      );
      if (!productSize) return;
      const variant = productSize[colorName].variantoption.find(
        (item: any) => item.id === variantId,
      );

      if (!variant) return;
      variant.qty = value;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductSizeData.pending, (state) => {
        state.loadingproductSize = true;
        state.error = "";
      })
      .addCase(fetchProductSizeData.fulfilled, (state, action) => {
        const { colorName, data } = action.payload;
        if (!colorName) return;

        state.loadingproductSize = false;
        state.productSizeData.unshift({ [colorName]: data });
        state.error = "";
      })
      .addCase(fetchProductSizeData.rejected, (state, action) => {
        state.loadingproductSize = false;
        state.productSizeData = [];
        state.error =
          action.error.message || "Failed to fetch product size data";
      });
    builder
      .addCase(fetchProductTierPrice.pending, (state) => {
        state.loadingproductTierPrice = true;
        state.error = "";
      })
      .addCase(fetchProductTierPrice.fulfilled, (state, action) => {
        state.loadingproductTierPrice = false;
        state.productTierPriceData = action.payload;
        state.error = "";
      })
      .addCase(fetchProductTierPrice.rejected, (state, action) => {
        state.loadingproductTierPrice = false;
        state.productTierPriceData = [];
        state.error =
          action.error.message || "Failed to fetch product tier price data";
      });
  },
});

export const {
  removeProductSizeData,
  resetProductSizeData,
  updateVariantOption,
} = productSizeSlice.actions;

export default productSizeSlice.reducer;
